/**
 * A site's Advanced tab (JSS#1158): the raw Kubernetes views, for Engineer+.
 *
 * This is what remains of the old Cluster tab. Its operator-facing sub-tabs
 * moved to the site's own tabs (Nodes, Releases, Health); what is left is the
 * kubectl-equivalent surface. SiteDetail lists the tab only for Engineer+, and
 * the tab wraps itself in <RoleGate> too, so a stray `?tab=advanced` renders
 * no Kubernetes panel for a lower persona. The BFF still enforces each
 * endpoint's role (defense in depth; this is the UI default).
 *
 * The sub-tab is controlled by the caller so it can live in the URL
 * (`?tab=advanced&sub=pods`).
 */
import { Role, canActAs } from '@jeket-cloud/types/auth';
import { RoleGate } from '@/components/auth/RoleGate';
import { useUserRoles } from '@/contexts/AuthContext';
import WorkloadsPanel from './WorkloadsPanel';
import PodsPanel from './PodsPanel';
import ServicesPanel from './ServicesPanel';
import StoragePanel from './StoragePanel';
import ResourcesPanel from './ResourcesPanel';
import SecretsPanel from './SecretsPanel';

export type AdvancedSubTab = 'workloads' | 'pods' | 'services' | 'storage' | 'resources' | 'secrets';

// Every sub-tab is Engineer+ through the tab itself. Secrets (#251) keeps its
// own Engineer gate so it stays Engineer-only if the tab's gate is lowered.
export const ADVANCED_SUB_TABS: readonly { key: AdvancedSubTab; label: string; minRole?: Role }[] = [
  { key: 'workloads', label: 'Workloads' },
  { key: 'pods', label: 'Pods' },
  { key: 'services', label: 'Services' },
  { key: 'storage', label: 'Storage' },
  { key: 'resources', label: 'Resources' },
  { key: 'secrets', label: 'Secrets', minRole: Role.Engineer },
];

export const DEFAULT_ADVANCED_SUB_TAB: AdvancedSubTab = 'workloads';

export function isAdvancedSubTab(value: string | null | undefined): value is AdvancedSubTab {
  return ADVANCED_SUB_TABS.some((t) => t.key === value);
}

interface Props {
  siteId: string;
  sub: AdvancedSubTab;
  onSubChange: (sub: AdvancedSubTab) => void;
}

export default function AdvancedTab({ siteId, sub, onSubChange }: Props) {
  const userRoles = useUserRoles();
  const visibleTabs = ADVANCED_SUB_TABS.filter((t) => !t.minRole || canActAs(userRoles, t.minRole));
  const active = visibleTabs.some((t) => t.key === sub) ? sub : DEFAULT_ADVANCED_SUB_TAB;

  return (
    <RoleGate role={Role.Engineer}>
      <div className="space-y-4" data-testid="site-advanced-tab">
        <div className="flex gap-1 border-b border-surface-elevated">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => onSubChange(tab.key)}
              data-testid={`advanced-subtab-${tab.key}`}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                active === tab.key
                  ? 'text-gold border-gold'
                  : 'text-silver-muted border-transparent hover:text-silver-bright'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {active === 'workloads' && <WorkloadsPanel siteId={siteId} />}
        {active === 'pods' && <PodsPanel siteId={siteId} />}
        {active === 'services' && <ServicesPanel siteId={siteId} />}
        {active === 'storage' && <StoragePanel siteId={siteId} />}
        {active === 'resources' && <ResourcesPanel siteId={siteId} />}
        {active === 'secrets' && <SecretsPanel siteId={siteId} />}
      </div>
    </RoleGate>
  );
}
